import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ActionButton, Buttons } from "../../components/Buttons";
import {
  TextInputform,
  DropDown,
  FormattedDate,
  Calender,
} from "../../components/Forms";
import { MdOutlineDelete } from "react-icons/md";
import { LiaEditSolid } from "react-icons/lia";
import { HiOutlineDotsVertical } from "react-icons/hi";
import PageTitle from "../../components/PageTitle";
import NotifyData from "../../components/NotifyData";
import Pagination from "../../components/Pagination";
import TableUI from "../../components/TableUI";
import {
  fetchCollections,
  deleteCollection,
} from "../../slice/CollectionSlice";
import { fetchCustomers } from "../../slice/CustomerSlice";
import * as XLSX from "xlsx";

const Collection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { collections } = useSelector((state) => state.collections);
  const { areas, staff } = useSelector((state) => state.customers);

  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const currentUserId = userData.user_id;

  // --- Filter States ---
  const [searchTerm, setSearchTerm] = useState("");
  const [recentFromDate, setRecentFromDate] = useState("");
  const [recentToDate, setRecentToDate] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [selectedStaff, setSelectedStaff] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const formatForAPI = (date) => date.toISOString().split("T")[0];

  const getDefaults = () => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return {
      yesterday: formatForAPI(yesterday),
      today: formatForAPI(today),
    };
  };

  useEffect(() => {
    dispatch(fetchCustomers());
    const { yesterday, today } = getDefaults();
    dispatch(
      fetchCollections({
        from_date: "",
        to_date: "",
        search_text: "",
      })
    );
  }, [dispatch]);

  // --- Handlers ---
  const handleApplyFilter = () => {
    dispatch(
      fetchCollections({
        from_date: recentFromDate,
        to_date: recentToDate,
        search_text: searchTerm,
        area_id: selectedArea,
        staff_id: selectedStaff,
      })
    );
    setCurrentPage(1);
  };

  const handleUndoFilter = () => {
    setRecentFromDate("");
    setRecentToDate("");
    setSearchTerm("");
    setSelectedArea("");
    setSelectedStaff("");
    const { yesterday, today } = getDefaults();
    dispatch(
      fetchCollections({
        from_date: "",
        to_date: "",
        search_text: "",
      })
    );
    setCurrentPage(1);
  };

  const handleCreate = () => navigate("/collection/create");
  const handleEdit = (col) => navigate(`/collection/edit/${col.collection_id}`);

  const handleDelete = async (collectionId) => {
    if (!currentUserId) {
      NotifyData("Operation Failed: User not authenticated", "error");
      return;
    }
    try {
      const msg = await dispatch(
        deleteCollection({ collectionId, currentUserId })
      ).unwrap();
      NotifyData(msg, "success");
      handleApplyFilter();
    } catch (error) {
      NotifyData(error.message, "error");
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = collections.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(collections.length / itemsPerPage);

  const headers = [
    "No",
    "Date",
    "Customer Name",
    "Box No",
    "Plan",
    "Entry Amount",
  ];
  const body = currentItems.map((collection, index) => ({
    key: collection.collection_id,
    values: [
      indexOfFirstItem + index + 1,
      FormattedDate(collection.collection_paid_date),
      collection.name,
      collection.box_no,
      collection.plan_name,
      collection.entry_amount,
      <ActionButton
        options={[
          {
            label: "Edit",
            icon: <LiaEditSolid />,
            onClick: () => handleEdit(collection),
          },
          {
            label: "Delete",
            icon: <MdOutlineDelete />,
            onClick: () => handleDelete(collection.collection_id),
          },
        ]}
        label={<HiOutlineDotsVertical />}
      />,
    ],
  }));

const handleDownloadExcel = () => {

    if (collections.length === 0) {
      NotifyData("No data available to download", "error");
      return;
    }

    // 2. Map all 239 records to your desired columns
    const excelData = collections.map((c, index) => ({
      "No": index + 1,
      "Date": c.collection_paid_date 
        ? new Date(c.collection_paid_date).toLocaleDateString("en-GB") 
        : "-",
      "Customer Name": c.name,
      "Box No": c.box_no,
      "Plan": c.plan_name,
      "Entry Amount": Number(c.entry_amount || 0),
    }));

    const totalAmount = collections.reduce((sum, item) => sum + Number(item.entry_amount || 0), 0);
    excelData.push({}); 
    excelData.push({
      "No": "TOTAL",
      "Date": "",
      "Customer Name": "",
      "Box No": "",
      "Plan": "",
      "Entry Amount": totalAmount,
    });

    // 5. Create and save the file
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Full Collection List");

    const today = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Collection_Full_Report_${today}.xlsx`);
    
    NotifyData(`Excel downloaded with all ${collections.length} records`, "success");
  };
  return (
    <div id="main">
      <Container fluid>
        <Row className="align-items-end">
          <Col xs="12" lg="6" className="py-3">
            <PageTitle PageTitle="Collection" showButton={false} />
          </Col>
          <Col
            xs="12"
            lg="6"
            className="py-3 text-end d-flex justify-content-end gap-2"
          >
            <Buttons
              btnlabel="Add New"
              className="add-btn"
              onClick={handleCreate}
            />
            <Button variant="success" onClick={handleDownloadExcel}>
              Download Excel
            </Button>
          </Col>

          {/* Filter Row 1 */}
          <Col lg="2" className="my-2">
            <Calender
              calenderlabel="From Date"
              initialDate={recentFromDate}
              setLabel={setRecentFromDate}
            />
          </Col>
          <Col lg="2" className="my-2">
            <Calender
              calenderlabel="To Date"
              initialDate={recentToDate}
              setLabel={setRecentToDate}
            />
          </Col>
          <Col lg="3" className="my-2">
            <DropDown
              textlabel="Select Area"
              options={areas.map((area) => ({
                label: area.area_name,
                value: area.area_id,
              }))}
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
            />
          </Col>
          <Col lg="3" className="my-2">
            <DropDown
              textlabel="Select Staff"
              options={staff.map((s) => ({
                label: s.staff_name,
                value: s.staff_id,
              }))}
              value={selectedStaff}
              onChange={(e) => setSelectedStaff(e.target.value)}
            />
          </Col>
          <Row className="align-items-end mt-2">
            <Col xs={12} lg={2} className="mb-3 mb-lg-0">
              <TextInputform
                textlabel="Search"
                PlaceHolder="Name or Customer No"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col xs={12} lg={3} className="mb-3 mb-lg-0">
              <div className="d-flex gap-2">
                <Button
                  variant="success"
                  className="flex-fill" 
                  onClick={handleApplyFilter}
                >
                  Apply
                </Button>
                <Button
                  variant="secondary"
                  className="flex-fill"
                  onClick={handleUndoFilter}
                >
                  Undo
                </Button>
              </div>
            </Col>
            <Col lg={3} className="d-none d-lg-block"></Col>
            <Col
              xs={12}
              lg={4}
              className="d-flex justify-content-center justify-content-lg-end"
            >
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </Col>
          </Row>

          <Col xs="12" className="py-3 tableui">
            <TableUI
              headers={headers}
              body={body}
              className="table"
              showActionColumn={true}
              noRecordMessage="No collections found"
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Collection;
